import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Typography,
  ThemeProvider,
  Divider,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Groups2Icon from '@mui/icons-material/Groups2';
import AddCircleIcon from "@mui/icons-material/AddCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import MapIcon from "@mui/icons-material/Map";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import InfoIcon from "@mui/icons-material/Info";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import LanguageIcon from "@mui/icons-material/Language";
import PlaceIcon from "@mui/icons-material/Place";
import EventNoteIcon from "@mui/icons-material/EventNote";
import TranslateIcon from "@mui/icons-material/Translate";
import logo from "../../component/logo/icon_new.png";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { getRole, logout } from "../auth";
import { travel_types_guest } from "../api/API";
import resources from "../../i18n";

const GREEN = "#2e7d32";

const logoSx = {
  height: 44,
  cursor: "pointer",
  filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.2))",
  transition: "transform 0.25s ease",
  "&:hover": { transform: "scale(1.05)" },
};

// ปุ่มเมนูแบบ pill — active = หน้าปัจจุบัน
const navButtonSx = (active) => ({
  px: 1.5,
  py: 0.75,
  borderRadius: 999,
  textTransform: "none",
  fontWeight: active ? 600 : 500,
  whiteSpace: "nowrap",
  bgcolor: active ? "rgba(255,255,255,0.22)" : "transparent",
  boxShadow: active ? "inset 0 0 0 1px rgba(255,255,255,0.35)" : "none",
  transition: "background-color 0.2s ease",
  "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
  "& .MuiButton-startIcon": { mr: 0.75 },
  "& .MuiButton-endIcon": { ml: 0.25 },
});

const pillSx = {
  px: 1.75,
  py: 0.6,
  mx: 0.5,
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 600,
  border: "1px solid rgba(255,255,255,0.35)",
  "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
};

const menuPaper = {
  elevation: 0,
  sx: {
    mt: 1.5,
    minWidth: 220,
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 16px 40px rgba(0,0,0,0.14)",
    "& .MuiMenuItem-root": {
      mx: 1,
      my: 0.25,
      borderRadius: 2,
      fontSize: 14,
      "&:hover": { bgcolor: "#f1f8e9", color: GREEN },
      "&.Mui-selected": { bgcolor: "#e8f5e9", color: GREEN, fontWeight: 600 },
    },
  },
};

// ไอคอนนำหน้ารายการย่อยในเมนู (มือถือ/iPad ให้เหมือน dropdown บน desktop)
const subIconSx = { mr: 1.5, color: "#9ccc65" };

const groupLabelSx = {
  opacity: "1 !important",
  mt: 1,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.5,
  color: "text.secondary",
  textTransform: "uppercase",
};

export default function HeadPage({ screenWidth, defaultTheme, isAuthenticated, }) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // เลื่อนลงแล้ว header เข้มขึ้น + มีเงา
  const [scrolled, setScrolled] = useState(window.scrollY > 10);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const isMobile = screenWidth < 400;
  const isIpad = screenWidth >= 401 && screenWidth < 900;
  const prefix = isMobile ? "img-mobile" : isIpad ? "img-ipad" : "img-web";
  // ใช้ t() แทน resources.store ตอน mount — ไฟล์แปลโหลดผ่าน http backend จึงยังว่างตอนแรก
  const districts = t("districts", { returnObjects: true });
  const districtList = Array.isArray(districts) ? districts : [];
  // เมนูเต็มแถวต้องกว้าง ~1100px — ต่ำกว่านั้น (มือถือ, iPad ทุกแนว) ใช้ hamburger
  const compactNav = screenWidth < 1150;

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElBaab, setAnchorElBaab] = useState(null);
  const [anchorLang, setAnchorLang] = useState(null);
  const [anchorMobileMenu, setAnchorMobileMenu] = useState(null);
  const [loginMenuAnchor, setLoginMenuAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const [travelType, setTravelType] = useState([]);
  const [loading, setLoading] = useState(false);
  const [languageData, setLanguageData] = useState(
    i18n.language === "th"
      ? resources?.store?.data?.th?.translation
      : resources?.store?.data?.en?.translation
  );
  const [language, setLanguage] = useState(i18n.language);
  // getRole() ก่อน — ถ้า token หมดอายุจะถูกลบทิ้งก่อนอ่าน
  const role = getRole();
  const token = localStorage.getItem("token");
  const hasToken = !!token;

  // ✅ โหลด travel type + subscribe language change ใน useEffect เดียว
  useEffect(() => {
    const loadTravels = async () => {
      setLoading(true);
      try {
        const res = await axios.get(travel_types_guest);
        setTravelType(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error loading travel types:", err);
        setTravelType([]);
      } finally {
        setLoading(false);
      }
    };

    // โหลดข้อมูลครั้งแรก
    loadTravels();

    // subscribe event เปลี่ยนภาษา
    const handleLanguageChange = (lng) => {
      console.log("Language changed:", lng);
      // ถ้าอยาก reload ข้อมูลตามภาษา ก็เรียก loadTravels() ที่นี่ได้
    };
    i18n.on("languageChanged", handleLanguageChange);

    // cleanup ตอน component unmount
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  const Logout = () => {
    logout();
    setUserMenuAnchor(null);
    navigate("/");
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng).then(() => {
      setLanguage(lng); // รีเฟรช UI
      setLanguageData(
        lng === "th"
          ? resources?.store?.data?.th?.translation
          : resources?.store?.data?.en?.translation
      );
    });
    handleLangClose();
  };

  // Menu handlers
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleMenuOpenBaab = (event) => setAnchorElBaab(event.currentTarget);
  const handleMenuCloseBaab = () => setAnchorElBaab(null);
  const handleLangOpen = (event) => setAnchorLang(event.currentTarget);
  const handleLangClose = () => setAnchorLang(null);
  const handleLoginMenuOpen = (event) => setLoginMenuAnchor(event.currentTarget);
  const handleLoginMenuClose = () => setLoginMenuAnchor(null);
  const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);

  const menuItems = [
    { type: "button", label: t("home"), icon: <HomeIcon fontSize="small" />, match: (p) => p === "/" },
    { type: "dropdown", label: t("routes"), icon: <MapIcon fontSize="small" />, match: (p) => p.startsWith("/district") },
    { type: "dropdownBaab", label: t("format"), icon: <ViewModuleIcon fontSize="small" />, match: (p) => p.startsWith("/format") },
    { type: "button", label: t("about"), icon: <InfoIcon fontSize="small" />, match: (p) => p === "/about" },
    { type: "button", label: t("contact"), icon: <ContactMailIcon fontSize="small" />, match: (p) => p === "/contact" },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          color: "white",
          background: scrolled
            ? "linear-gradient(90deg, rgba(27,94,32,0.88) 0%, rgba(85,139,47,0.88) 100%)"
            : "linear-gradient(90deg, #2e7d32 0%, #7cb342 100%)",
          backdropFilter: "saturate(180%) blur(14px)",
          WebkitBackdropFilter: "saturate(180%) blur(14px)",
          boxShadow: scrolled ? "0 8px 28px rgba(0,0,0,0.18)" : "0 1px 0 rgba(255,255,255,0.15)",
          transition: "background 0.35s ease, box-shadow 0.35s ease",
        }}
      >
        <Toolbar sx={{ height: 64, display: "flex", justifyContent: "space-between", px: { xs: 2, md: 3 } }}>
          {compactNav ? (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Box component="img" src={logo} alt="Logo" onClick={() => navigate("/")} sx={logoSx} />
              <IconButton
                color="inherit"
                onClick={(e) => setAnchorMobileMenu(e.currentTarget)}
                sx={{ bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorMobileMenu}
                open={Boolean(anchorMobileMenu)}
                onClose={() => setAnchorMobileMenu(null)}
                PaperProps={{ ...menuPaper, sx: { ...menuPaper.sx, minWidth: 260, maxHeight: "75vh" } }}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={() => { navigate("/"); setAnchorMobileMenu(null); }}>
                  <HomeIcon fontSize="small" sx={{ mr: 1.5, color: GREEN }} /> {t("home")}
                </MenuItem>

                <MenuItem disabled sx={groupLabelSx}><MapIcon fontSize="small" sx={{ mr: 1.5, color: GREEN }} />{t("routes")}</MenuItem>
                {districtList.map((t, idx) => {
                  return (
                    <MenuItem key={`district-${idx}`} sx={{ pl: 4 }} onClick={() => { navigate(`/district/${t.id}`); setAnchorMobileMenu(null); }}>
                      <PlaceIcon fontSize="small" sx={subIconSx} />
                      {language === "th"
                        ? t?.name_th
                        : t?.name_en}
                    </MenuItem>
                  )
                })}
                <MenuItem disabled sx={groupLabelSx}><ViewModuleIcon fontSize="small" sx={{ mr: 1.5, color: GREEN }} />{t("format")}</MenuItem>
                {travelType?.map((t, idx) => {
                  const label = language === "en" ? (t.name_en || t.name) : t.name;
                  return (
                    <MenuItem key={`format-${idx}`} sx={{ pl: 4 }} onClick={() => { navigate(`/format/${t.ttid}?name=${encodeURIComponent(label)}`); setAnchorMobileMenu(null); }}>
                      <EventNoteIcon fontSize="small" sx={subIconSx} />
                      {label}
                    </MenuItem>
                  );
                })}

                <Divider sx={{ my: 1 }} />
                {(hasToken && role !== "guest") && (
                  <MenuItem onClick={() => { navigate("/tour"); setAnchorMobileMenu(null); }}>
                    <Groups2Icon fontSize="small" sx={{ mr: 1.5, color: GREEN }} /> {t("group_tour")}
                  </MenuItem>
                )}

                <MenuItem onClick={() => { navigate("/about"); setAnchorMobileMenu(null); }}>
                  <InfoIcon fontSize="small" sx={{ mr: 1.5, color: GREEN }} /> {t("about")}
                </MenuItem>
                <MenuItem onClick={() => { navigate("/contact"); setAnchorMobileMenu(null); }}>
                  <ContactMailIcon fontSize="small" sx={{ mr: 1.5, color: GREEN }} /> {t("contact")}
                </MenuItem>

                <MenuItem disabled sx={groupLabelSx}><LanguageIcon fontSize="small" sx={{ mr: 1.5, color: GREEN }} />{t("language")}</MenuItem>
                <MenuItem selected={language === "th"} sx={{ pl: 4 }} onClick={() => { changeLanguage("th"); setAnchorMobileMenu(null); }}>
                  <TranslateIcon fontSize="small" sx={subIconSx} />ไทย
                </MenuItem>
                <MenuItem selected={language === "en"} sx={{ pl: 4 }} onClick={() => { changeLanguage("en"); setAnchorMobileMenu(null); }}>
                  <TranslateIcon fontSize="small" sx={subIconSx} />English
                </MenuItem>

                <Divider sx={{ my: 1 }} />
                <MenuItem disabled sx={{ opacity: "1 !important", fontSize: 13 }}>
                  <AccountCircleIcon fontSize="small" sx={{ mr: 1.5, color: GREEN }} />
                  {t("current_role")}: <strong style={{ marginLeft: 4 }}>{t(role)}</strong>
                </MenuItem>
                {(!token && role === "guest") && (
                  <MenuItem onClick={() => { navigate("/login"); setAnchorMobileMenu(null); }}>
                    <LoginIcon fontSize="small" sx={{ mr: 1.5, color: GREEN }} /> {t("login")}
                  </MenuItem>
                )}
                {token && (
                  <MenuItem onClick={() => { Logout(); setAnchorMobileMenu(null); }} sx={{ color: "#d32f2f" }}>
                    <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} /> {t("logout")}
                  </MenuItem>
                )}
              </Menu>
            </Box>
          ) : (
            <>
              <Box component="img" src={logo} alt="Logo" onClick={() => navigate("/")} sx={logoSx} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {menuItems?.map((item, idx) => (
                  <Button
                    key={idx}
                    color="inherit"
                    startIcon={item.icon}
                    onClick={(e) => {
                      if (item.type === "dropdown") handleMenuOpen(e);
                      else if (item.type === "dropdownBaab") handleMenuOpenBaab(e);
                      else if (item.label === t("home")) navigate("/");
                      else if (item.label === t("about")) navigate("/about");
                      else if (item.label === t("contact")) navigate("/contact");
                    }}
                    endIcon={["dropdown", "dropdownBaab"].includes(item.type) ? <KeyboardArrowDownIcon fontSize="small" /> : null}
                    sx={navButtonSx(item.match(location.pathname))}
                  >
                    {item.label}
                  </Button>
                ))}

                {(hasToken && role !== "guest") && (
                  <Button
                    color="inherit"
                    startIcon={<Groups2Icon fontSize="small" />}
                    onClick={() => navigate("/tour")}
                    sx={navButtonSx(location.pathname.startsWith("/tour"))}
                  >
                    {t("group_tour")}
                  </Button>
                )}

                <Box sx={{ width: "1px", height: 28, bgcolor: "rgba(255,255,255,0.3)", mx: 1 }} />

                <Button
                  color="inherit"
                  onClick={handleLangOpen}
                  startIcon={<LanguageIcon fontSize="small" />}
                  endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                  sx={{ ...pillSx, minWidth: 0 }}
                >
                  {language?.toUpperCase().slice(0, 2)}
                </Button>

                {/* 🔹 เมนูสำหรับผู้ใช้ */}
                {!hasToken ? (
                  // ✅ ยังไม่ได้ล็อกอิน (Guest)
                  <>
                    <Button
                      color="inherit"
                      startIcon={<AccountCircleIcon />}
                      onClick={handleLoginMenuOpen}
                      sx={{ ...pillSx, bgcolor: "white", color: GREEN, "&:hover": { bgcolor: "#f1f8e9" } }}
                      endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                    >
                      {t("guest")}
                    </Button>

                    <Menu
                      anchorEl={loginMenuAnchor}
                      open={Boolean(loginMenuAnchor)}
                      onClose={handleLoginMenuClose}
                      PaperProps={menuPaper}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem
                        onClick={() => {
                          navigate("/login");
                          handleLoginMenuClose();
                        }}
                      >
                        <LoginIcon fontSize="small" sx={{ mr: 1.5, color: GREEN }} />
                        {t("login")}
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  // ✅ ล็อกอินแล้ว (admin หรือ user)
                  <>
                    <Button
                      color="inherit"
                      startIcon={<AccountCircleIcon />}
                      onClick={handleUserMenuOpen}
                      sx={{ ...pillSx, bgcolor: "white", color: GREEN, "&:hover": { bgcolor: "#f1f8e9" } }}
                      endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                    >
                      {t(role)} {/* เช่น admin / user */}
                    </Button>

                    <Menu
                      anchorEl={userMenuAnchor}
                      open={Boolean(userMenuAnchor)}
                      onClose={handleUserMenuClose}
                      PaperProps={menuPaper}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      {/* <MenuItem
                        onClick={() => {
                          navigate(role === "admin" ? "/admin" : "/user");
                          handleUserMenuClose();
                        }}
                      >
                        <AddCircleIcon fontSize="small" sx={{ mr: 1 }} />
                        {t("dashboard")}
                      </MenuItem> */}
                      <MenuItem
                        onClick={() => {
                          Logout();
                          handleUserMenuClose();
                        }}
                        sx={{ color: "#d32f2f" }}
                      >
                        <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
                        {t("logout")}
                      </MenuItem>
                    </Menu>
                  </>
                )}


                {/* Dropdowns Desktop */}
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={menuPaper}>
                  {districtList.map((t, idx) => {
                    return (
                      <MenuItem key={`district-${idx}`} onClick={() => { navigate(`/district/${t.id}`); handleMenuClose(); }}>
                        <MapIcon fontSize="small" sx={{ mr: 1.5, color: "#9ccc65" }} />
                        {language === "th"
                          ? t?.name_th
                          : t?.name_en}
                      </MenuItem>
                    )
                  })}
                </Menu>

                <Menu anchorEl={anchorElBaab} open={Boolean(anchorElBaab)} onClose={handleMenuCloseBaab} PaperProps={menuPaper}>
                  {travelType?.map((t, idx) => {
                    const label = language === "en" ? (t.name_en || t.name) : t.name;
                    return (
                      <MenuItem key={`format-${idx}`} onClick={() => { navigate(`/format/${t.ttid}?name=${encodeURIComponent(label)}`); handleMenuCloseBaab(); }}>
                        <ViewModuleIcon fontSize="small" sx={{ mr: 1.5, color: "#9ccc65" }} />
                        {label}
                      </MenuItem>
                    );
                  })}
                </Menu>

                <Menu
                  anchorEl={anchorLang}
                  open={Boolean(anchorLang)}
                  onClose={handleLangClose}
                  PaperProps={{ ...menuPaper, sx: { ...menuPaper.sx, minWidth: 160 } }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem selected={language === "th"} onClick={() => changeLanguage("th")}>ไทย</MenuItem>
                  <MenuItem selected={language === "en"} onClick={() => changeLanguage("en")}>English</MenuItem>
                </Menu>
              </Box>
            </>
          )}
        </Toolbar>
      </Box>
    </ThemeProvider>
  );
}
